import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { dataService } from '../services/data-service';

interface IntegrationSettings {
  integrationType: string;
  instanceId?: string;
  instanceName?: string;
  enabled: boolean;
}

interface IntegrationContextType {
  settings: IntegrationSettings | null;
  isLoading: boolean;
  error: string | null;
  setSelectedInstance: (integrationType: string, instanceId: string, instanceName: string, enabled: boolean) => Promise<void>;
  clearSelectedInstance: (integrationType: string) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const IntegrationContext = createContext<IntegrationContextType | undefined>(undefined);

export function IntegrationProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [settings, setSettings] = useState<IntegrationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await dataService.getIntegrationSettings();
      if (result) {
        setSettings({
          integrationType: result.integrationType,
          instanceId: result.instanceId,
          instanceName: result.instanceName,
          enabled: result.enabled,
        });
      } else {
        setSettings(null);
      }
    } catch (err) {
      console.error('Failed to load integration settings:', err);
      setError('Failed to load integration settings');
      setSettings(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const setSelectedInstance = useCallback(async (
    integrationType: string,
    instanceId: string,
    instanceName: string,
    enabled: boolean
  ) => {
    try {
      setError(null);
      const result = await dataService.saveIntegrationSettings({
        integrationType,
        instanceId,
        instanceName,
        enabled,
      });
      setSettings({
        integrationType: result.integrationType,
        instanceId: result.instanceId,
        instanceName: result.instanceName,
        enabled: result.enabled,
      });
    } catch (err) {
      console.error('Failed to save integration settings:', err);
      setError('Failed to save integration settings');
      throw err;
    }
  }, []);

  const clearSelectedInstance = useCallback(async (integrationType: string) => {
    try {
      setError(null);
      await dataService.saveIntegrationSettings({
        integrationType,
        instanceId: undefined,
        instanceName: undefined,
        enabled: false,
      });
      setSettings(null);
    } catch (err) {
      console.error('Failed to clear integration settings:', err);
      setError('Failed to clear integration settings');
      throw err;
    }
  }, []);

  const value: IntegrationContextType = {
    settings,
    isLoading,
    error,
    setSelectedInstance,
    clearSelectedInstance,
    refreshSettings,
  };

  return <IntegrationContext.Provider value={value}>{children}</IntegrationContext.Provider>;
}

export function useIntegration(): IntegrationContextType {
  const context = useContext(IntegrationContext);
  if (context === undefined) {
    throw new Error('useIntegration must be used within an IntegrationProvider');
  }
  return context;
}
